import { z } from "zod";

/**
 * A website someone is about to save a login for, as both ends read it. The browser parses what
 * was typed to show a hint; the server parses it again before fetching anything, with the same
 * rules, so the two can never disagree about what is acceptable.
 *
 * Only https on the default port is accepted: a teammate will type a password into that page, and
 * a server that fetches user-supplied addresses should not be steered at other ports. Credentials
 * in the address, IP literals and hosts without a dot are refused.
 */
export type SiteAddress =
  | { ok: true; url: string; host: string }
  | { ok: false; reason: "invalid" | "insecure" };

const HOST_LABEL = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;
const IPV4_LITERAL = /^\d{1,3}(?:\.\d{1,3}){3}$/;

export function parseSiteAddress(input: string): SiteAddress {
  const trimmed = input.trim();
  if (!trimmed || trimmed.length > URL_LIMIT) return { ok: false, reason: "invalid" };
  const hasScheme = /^[a-z][a-z0-9+.-]*:/i.test(trimmed);
  let url: URL;
  try {
    url = new URL(hasScheme ? trimmed : `https://${trimmed}`);
  } catch {
    return { ok: false, reason: "invalid" };
  }
  if (url.protocol === "http:") return { ok: false, reason: "insecure" };
  if (url.protocol !== "https:" || url.username || url.password || url.port) {
    return { ok: false, reason: "invalid" };
  }
  const hostname = url.hostname.toLowerCase();
  const labels = hostname.split(".");
  if (
    labels.length < 2 ||
    hostname.length > 253 ||
    hostname.startsWith("[") ||
    IPV4_LITERAL.test(hostname) ||
    !labels.every((label) => HOST_LABEL.test(label)) ||
    /^\d+$/.test(labels.at(-1) ?? "")
  ) {
    return { ok: false, reason: "invalid" };
  }
  // The query and fragment stay: a sign-in page may need `?tenant=…` or a hash route.
  const rest = `${url.search}${url.hash}`;
  const path = url.pathname === "/" && !rest ? "" : url.pathname;
  return { ok: true, url: `${url.origin}${path}${rest}`, host: hostname.replace(/^www\./, "") };
}

export const URL_LIMIT = 2048;

/** The largest icon the server returns, before encoding. Real favicons are a few kilobytes. */
export const ICON_BYTE_LIMIT = 100_000;
const DATA_URL_LIMIT = Math.ceil(ICON_BYTE_LIMIT / 3) * 4 + 64;

/**
 * The image types an icon may be. SVG is allowed: shown through `<img>`, an SVG runs no scripts
 * and loads nothing else.
 */
export const ICON_TYPES = [
  "image/png",
  "image/x-icon",
  "image/jpeg",
  "image/gif",
  "image/webp",
  "image/svg+xml",
] as const;
export type IconType = (typeof ICON_TYPES)[number];

/** An icon as the API returns it and the browser may keep it: an allowed image, inline, bounded. */
export const iconDataUrlSchema = z
  .string()
  .max(DATA_URL_LIMIT)
  .regex(/^data:image\/(png|x-icon|jpeg|gif|webp|svg\+xml);base64,[A-Za-z0-9+/]+={0,2}$/);

export const sitePreviewQuerySchema = z.object({
  url: z
    .string()
    .max(URL_LIMIT)
    .refine((value) => parseSiteAddress(value).ok, { error: "Enter an https address." }),
});

/**
 * What the server found at an address. Either part may be missing: plenty of portals have no
 * title worth showing or no icon, and a site that cannot be reached answers with neither — the
 * same answer as one the server would not fetch, so the reply says nothing about why.
 */
export const sitePreviewSchema = z.object({
  host: z.string().min(1).max(253),
  title: z.string().max(200).nullable(),
  icon: z.object({ dataUrl: iconDataUrlSchema }).nullable(),
});
export type SitePreview = z.infer<typeof sitePreviewSchema>;
